/**
 * src/components/MoreInfoPill.js
 *
 * The ONE bottom-of-card pill that opens a product's Dashboard detail page.
 *
 * Every product card that has a detail page uses this component, so the CTA is
 * visually and behaviourally identical on Paid Utilities, Security and RPG
 * Worlds. It is deliberately distinct from Launch / Download / Purchase / Run /
 * Start controls: it only opens the deeper Dashboard information page and never
 * grants, buys or starts anything.
 *
 * Product accent colours differ; the pill geometry, weight, label and behaviour
 * do not.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { glassSurface, viGlassShadow } from '../config/glassTheme';
import { productDetailPath, MORE_INFO_LABEL, PRODUCT_DETAILS } from '../config/productDetails';
import { PRODUCT_PAGES } from '../productPages';

export function resolveMoreInfoDestination(destination, pathname = window.location.pathname) {
  if (pathname.startsWith('/preview/')) {
    return destination.replace(/^\/dashboard\//, '/preview/');
  }
  return destination;
}

export default function MoreInfoPill({ productKey, route, accent = '#38bdf8', style }) {
  const navigate = useNavigate();
  const [hover, setHover] = useState(false);

  // Standard product destinations are exposed from the reviewed detail registries. A small number
  // of products (for example AI Rebuild) use a dedicated information-and-pricing route instead; the
  // explicit route prop lets those cards use the same visible More Info control without inventing a
  // duplicate /dashboard/product page.
  if (!route && !PRODUCT_PAGES[productKey] && !PRODUCT_DETAILS[productKey]) return null;

  const destination = route || productDetailPath(productKey);

  const base = {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 999,
    padding: '12px 20px',
    fontSize: 12.5,
    fontWeight: 900,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    textAlign: 'center',
    cursor: 'pointer',
    ...glassSurface(accent, accent, accent),
    boxShadow: viGlassShadow(accent, accent, accent, hover),
    transform: hover ? 'scale(1.02)' : 'scale(1)',
    transition: 'transform 160ms cubic-bezier(0.22,1,0.36,1), background 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
    outlineOffset: 3,
  };

  return (
    <button
      type="button"
      className="vi-more-info-pill"
      style={{ ...base, ...(style || {}) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      onClick={() => navigate(resolveMoreInfoDestination(destination))}
    >
      {MORE_INFO_LABEL}
    </button>
  );
}
